import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CalendarCheck, ChevronDown, Clock, MapPin, Phone, ShoppingBag, Star } from "lucide-react";
import { BOOK_URL, IMAGES, ORDER_URL, PHONE_DISPLAY, PHONE_TEL } from "./data";

const EASE = [0.22, 1, 0.36, 1];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: 0.75 + 0.12 * i, duration: 0.8, ease: EASE } }),
};

const lineReveal = {
  hidden: { y: "115%", rotate: 2.5 },
  show: (i) => ({ y: "0%", rotate: 0, transition: { delay: 0.2 + i * 0.18, duration: 1.05, ease: EASE } }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.06, 1.22]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "45%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="grain-overlay relative flex min-h-[100svh] items-center overflow-hidden">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 will-change-transform">
        <img
          src={IMAGES.hero}
          alt="Fresh Indian curries and basmati rice served at Curry Story"
          className="h-full w-full object-cover"
          fetchpriority="high"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(253,252,249,0.55) 0%, rgba(253,252,249,0.05) 45%, rgba(253,250,244,0.92) 100%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(100deg, rgba(253,252,249,0.94) 0%, rgba(253,252,249,0.78) 38%, rgba(253,252,249,0) 72%)",
          }}
        />
      </motion.div>

      <motion.div style={{ y: contentY, opacity: contentOpacity }} className="relative z-10 mx-auto w-full max-w-6xl px-4 pb-40 pt-28 sm:px-6 sm:pb-36 sm:pt-32 will-change-transform">
        <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0}
          className="inline-flex items-center gap-2 rounded-full border border-[#C97B5F]/30 bg-white/70 px-4 py-1.5 backdrop-blur-md">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
          </span>
          <span data-testid="hero-open-status" className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6B5320]/90">
            Open Daily 4:30 PM – 8:45 PM
          </span>
        </motion.div>

        <h1 className="mt-6 font-display text-4xl font-black leading-[1.08] tracking-tight text-[#2A211A] sm:text-5xl lg:text-6xl">
          <span className="block overflow-hidden pb-1">
            <motion.span variants={lineReveal} initial="hidden" animate="show" custom={0} className="block will-change-transform">
              Every Curry Tells
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span variants={lineReveal} initial="hidden" animate="show" custom={1} className="text-gold-gradient block italic will-change-transform">
              a Story.
            </motion.span>
          </span>
        </h1>

        <motion.p
          variants={fadeUp} initial="hidden" animate="show" custom={2}
          className="mt-5 max-w-xl text-base font-light leading-relaxed text-[#3A2C1E]/90 sm:text-lg"
        >
          Authentic Indian dining in the heart of Chapel Hill — slow-simmered curries, clay-tandoor kebabs and naan baked fresh to order.
        </motion.p>

        <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-4 flex items-center gap-4 text-xs text-stone-600">
          <span className="flex items-center gap-1.5">
            <Star size={13} className="fill-[#E86A24] text-[#8A3324]" /> Loved by locals
          </span>
          <span className="flex items-center gap-1.5">
            <MapPin size={13} className="text-[#8A3324]" /> Chapel Hill, QLD
          </span>
        </motion.div>

        <motion.div variants={fadeUp} initial="hidden" animate="show" custom={4} className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
          <a
            href={ORDER_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-order-online-button"
            className="flex items-center justify-center gap-2.5 rounded-full bg-gradient-to-r from-[#E86A24] to-[#B82E1F] px-8 py-4 text-sm font-bold uppercase tracking-widest text-white shadow-xl shadow-[#E86A24]/40 transition-transform duration-200 hover:scale-[1.03] active:scale-[0.98]"
          >
            <ShoppingBag size={17} /> Order Online
          </a>
          <a
            href={BOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-book-table-button"
            className="flex items-center justify-center gap-2.5 rounded-full border border-[#C97B5F] bg-white/50 px-8 py-4 text-sm font-bold uppercase tracking-widest text-[#8A3324] backdrop-blur-sm transition-colors duration-200 hover:bg-[#D4A359]/10 active:scale-[0.98]"
          >
            <CalendarCheck size={17} /> Book a Table
          </a>
        </motion.div>

        <motion.div variants={fadeUp} initial="hidden" animate="show" custom={5} className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-stone-600">
          <a href={PHONE_TEL} data-testid="hero-phone-number-link" className="flex items-center gap-2 transition-colors duration-200 hover:text-[#8A3324]">
            <Phone size={15} className="text-[#8A3324]" /> {PHONE_DISPLAY}
          </a>
          <span className="flex items-center gap-2">
            <Clock size={15} className="text-[#8A3324]" /> Dinner from 4:30 PM
          </span>
        </motion.div>
      </motion.div>

      <a href="#about" data-testid="hero-scroll-down-indicator" aria-label="Scroll to about section"
        className="absolute bottom-24 left-1/2 z-10 hidden -translate-x-1/2 text-[#8A3324]/70 sm:bottom-8 sm:block">
        <ChevronDown size={26} className="animate-bounce" />
      </a>
    </section>
  );
}
